import { useState, useEffect, forwardRef } from 'react';
import { Video, ExternalLink } from 'lucide-react';
import { ContentModal } from './ContentModal';
import { Button } from '../button/Button';

interface VideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVideoSelect: (videoUrl: string) => void;
  zIndex?: number;
}

export const VideoUploadModal = forwardRef<HTMLDivElement, VideoUploadModalProps>(
  ({ isOpen, onClose, onVideoSelect, zIndex = 99999 }, _ref) => {
    const [urlInput, setUrlInput] = useState('');
    const [error, setError] = useState('');
    const [embedUrl, setEmbedUrl] = useState('');

    const getEmbedUrl = (inputUrl: string): string => {
      // Convert YouTube URLs
      const youtubeMatch = inputUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
      if (youtubeMatch) {
        return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
      }
      
      // Convert Vimeo URLs
      const vimeoMatch = inputUrl.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
      }
      
      // If it's already an embed URL or other format, use as is
      return inputUrl;
    };

    const handleUrlChange = (url: string) => {
      setUrlInput(url);
      setError('');
      
      if (url.trim()) {
        const embed = getEmbedUrl(url.trim());
        setEmbedUrl(embed);
      } else {
        setEmbedUrl('');
      }
    };

    const handleUrlSubmit = () => {
      if (urlInput.trim()) {
        const finalUrl = getEmbedUrl(urlInput.trim());
        onVideoSelect(finalUrl);
        onClose();
      }
    };

    const isValidVideoUrl = (url: string): boolean => {
      const patterns = [
        /youtube\.com\/watch\?v=/,
        /youtu\.be\//,
        /vimeo\.com\/\d+/,
        /\.mp4$/,
        /\.webm$/,
        /\.ogg$/
      ];
      return patterns.some(pattern => pattern.test(url));
    };

    // Force close any open tippy popovers when modal opens
    useEffect(() => {
      if (isOpen) {
        // Hide all tippy instances
        const tippyInstances = document.querySelectorAll('[data-tippy-root]');
        tippyInstances.forEach(instance => {
          const element = instance as any;
          if (element._tippy) {
            element._tippy.hide();
          }
        });
      }
    }, [isOpen]);

    return (
      <ContentModal
        isOpen={isOpen}
        onClose={onClose}
        title="Add Video"
        size="lg"
        zIndex={zIndex}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUrlSubmit}
              disabled={!urlInput.trim() || !isValidVideoUrl(urlInput)}
            >
              Add Video
            </Button>
          </div>
        }
      >
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* URL Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Video URL
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
              className="field-text flex-1 px-3 py-2 border border-field rounded-md bg-background dark:bg-white/5 text-gray-900 dark:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUrlSubmit();
                }
              }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Supports YouTube, Vimeo, and direct video file URLs
          </p>
        </div>

        {/* Preview */}
        {embedUrl && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Preview
            </label>
            <div className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border">
              <iframe
                src={embedUrl}
                title="Video preview"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Supported Formats */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Video className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                Supported Video Sources
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li className="flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />
                  YouTube videos (youtube.com, youtu.be)
                </li>
                <li className="flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />
                  Vimeo videos (vimeo.com)
                </li>
                <li className="flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />
                  Direct video files (.mp4, .webm, .ogg)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </ContentModal>
    );
  }
);

VideoUploadModal.displayName = 'VideoUploadModal';